/**
 * Helper functions for vendor name formatting and manipulation
 */

/**
 * Cleans a vendor name by removing location indicators, hyphens, and properly capitalizing
 * @param vendorName The vendor name to clean
 * @returns Cleaned and properly formatted vendor name
 */
export const cleanVendorName = (vendorName: string): string => {
    if (!vendorName) return ''

    // Define known suffixes to remove (common domain suffixes, business entities, and country codes)
    const knownSuffixes = [
        // Common domain and business suffixes
        'com', 'co', 'inc', 'ltd', 'llc', 'corp',
        // Common country codes and variations
        'uk', 'us', 'usa', 'ca', 'au', 'nz', 'ie', 'fr', 'de', 'it', 'es', 'pt',
        'jp', 'cn', 'kr', 'ru', 'br', 'mx', 'in', 'sg', 'my', 'th', 'vn', 'ph',
        'id', 'fi', 'no', 'se', 'dk', 'nl', 'be', 'ch', 'at', 'pl', 'cz', 'gr',
        'tr', 'ae', 'sa', 'za'
    ];

    // Create a pattern that matches any of the known suffixes at the end of the string
    // with optional preceding space, dot, or hyphen
    const suffixPattern = new RegExp(`[\\s.-]+(${knownSuffixes.join('|')})$`, 'i');

    // Iteratively remove suffixes until no more matches
    let cleaned = vendorName;
    while (suffixPattern.test(cleaned)) {
        cleaned = cleaned.replace(suffixPattern, '');
    }

    cleaned = cleaned
        .replace(/-/g, ' ') // Replace remaining hyphens with spaces
        .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
        .trim();

    return cleaned
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
}